import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, X, Loader2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const inputCls = "w-full rounded-lg border border-[var(--njas-border)] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-njas-sage focus:border-njas-sage";

const SEVERITY_STYLE = {
  low: "bg-gray-100 text-gray-700",
  medium: "bg-amber-50 text-amber-700",
  high: "bg-orange-100 text-orange-700",
  critical: "bg-red-100 text-red-700 border border-red-200",
};
const STATUS_STYLE = {
  new: "bg-red-50 text-red-700 border border-red-200",
  acknowledged: "bg-yellow-50 text-yellow-700",
  resolved: "bg-emerald-50 text-emerald-700",
};

export default function Incidents() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(() => {
    setLoading(true);
    const params = statusFilter === "all" ? {} : { status_filter: statusFilter };
    return api.get("/incidents", { params }).then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/incidents/${id}`, { status }); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="fade-in" data-testid="incidents-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="eyebrow text-njas-sage-hover text-[10px]">Safety</div>
          <h2 className="font-serif text-3xl text-njas-navy">Incident Reports</h2>
        </div>
        <div className="flex gap-2">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-lg border border-[var(--njas-border)] bg-white text-sm py-2 px-3">
            <option value="all">All</option><option value="new">New</option><option value="acknowledged">Acknowledged</option><option value="resolved">Resolved</option>
          </select>
          <button onClick={() => setShowNew(true)} data-testid="new-incident-btn" className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-5 py-2.5 text-sm hover:bg-njas-navy-hover">
            <Plus className="w-4 h-4" /> Report incident
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {loading && <div className="text-sm text-njas-muted">Loading…</div>}
        {!loading && rows.length === 0 && <div className="text-sm text-njas-muted">No incident reports.</div>}
        {rows.map((r) => (
          <div key={r.id} className="rounded-xl bg-white border border-[var(--njas-border)] p-5" data-testid={`incident-${r.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <AlertTriangle className={`w-4 h-4 ${r.severity === "critical" ? "text-red-600" : r.severity === "high" ? "text-orange-600" : "text-njas-muted"}`} />
                <span className="capitalize font-serif text-lg text-njas-navy">{r.incident_type?.replace(/_/g, " ")}</span>
                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${SEVERITY_STYLE[r.severity] || "bg-gray-100 text-gray-700"}`}>{r.severity}</span>
                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${STATUS_STYLE[r.status] || "bg-gray-100 text-gray-700"}`}>{r.status}</span>
                {r.client_id && <Link to={`/dashboard/clients/${r.client_id}`} className="text-xs text-njas-navy hover:underline">· {r.client_name}</Link>}
              </div>
              {user?.role !== "peer_worker" && (
                <div className="flex gap-2">
                  {r.status === "new" && <button onClick={() => setStatus(r.id, "acknowledged")} className="text-xs text-njas-navy hover:underline">Acknowledge</button>}
                  {r.status !== "resolved" && <button onClick={() => setStatus(r.id, "resolved")} className="text-xs text-emerald-700 hover:underline">Resolve</button>}
                </div>
              )}
            </div>
            <p className="text-sm text-njas-text whitespace-pre-wrap mb-2">{r.description}</p>
            {r.actions_taken && <div className="text-sm"><span className="text-njas-muted">Actions: </span>{r.actions_taken}</div>}
            <div className="text-xs text-njas-muted mt-2">
              Reported by {r.reported_by_name} ({r.reported_by_role}) · {new Date(r.incident_date || r.created_at).toLocaleString("en-AU")}
            </div>
          </div>
        ))}
      </div>

      {showNew && <NewIncidentModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewIncidentModal({ onClose, onSaved }) {
  const [clients, setClients] = useState([]);
  const [f, set] = useState({
    client_id: "", incident_date: new Date().toISOString().slice(0, 16),
    incident_type: "aggression", severity: "medium",
    location: "", description: "", actions_taken: "",
    followup_needed: false, followup_notes: "", notifications_made: "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => set({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        ...f,
        client_id: f.client_id || null,
        incident_date: f.incident_date ? new Date(f.incident_date).toISOString() : null,
        notifications_made: f.notifications_made.split(",").map((s) => s.trim()).filter(Boolean),
      };
      await api.post("/incidents", body);
      toast.success("Incident reported"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 py-10 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div><div className="eyebrow text-njas-sage-hover text-[10px]">Safety</div><h3 className="font-serif text-2xl text-njas-navy">Report an incident</h3></div>
          <button onClick={onClose}><X className="w-5 h-5 text-njas-muted" /></button>
        </div>
        <form onSubmit={save} className="space-y-3" data-testid="incident-form">
          <div className="grid grid-cols-2 gap-3">
            <label className="block col-span-2"><span className="text-xs uppercase tracking-wider text-njas-navy">Client (optional)</span>
              <select value={f.client_id} onChange={upd("client_id")} className={inputCls}>
                <option value="">— No client / general —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
            </label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">When did it happen</span><input type="datetime-local" required value={f.incident_date} onChange={upd("incident_date")} className={inputCls} /></label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Location</span><input value={f.location} onChange={upd("location")} className={inputCls} placeholder="e.g. Home visit, office…" /></label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Incident type</span>
              <select value={f.incident_type} onChange={upd("incident_type")} className={inputCls} required>
                <option value="aggression">Aggression / threat</option>
                <option value="self_harm">Self-harm</option>
                <option value="medical">Medical emergency</option>
                <option value="disclosure">Disclosure (abuse / harm)</option>
                <option value="property_damage">Property damage</option>
                <option value="absconding">Absconding / missing</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Severity</span>
              <select value={f.severity} onChange={upd("severity")} className={inputCls}>
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
              </select>
            </label>
          </div>
          <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">What happened *</span><textarea rows={4} required value={f.description} onChange={upd("description")} className={inputCls} /></label>
          <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Actions taken</span><textarea rows={2} value={f.actions_taken} onChange={upd("actions_taken")} className={inputCls} /></label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.followup_needed} onChange={upd("followup_needed")} /> Follow-up needed
          </label>
          {f.followup_needed && <textarea rows={2} value={f.followup_notes} onChange={upd("followup_notes")} placeholder="Follow-up notes / plan" className={inputCls} />}
          <label className="block"><span className="text-xs uppercase tracking-wider text-njas-navy">Notifications made (comma-separated)</span><input value={f.notifications_made} onChange={upd("notifications_made")} placeholder="e.g. Manager, DCJ, Police" className={inputCls} /></label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-full px-5 py-2 text-sm text-njas-muted hover:text-njas-navy">Cancel</button>
            <button type="submit" disabled={busy} data-testid="incident-submit" className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-6 py-2 text-sm hover:bg-njas-navy-hover disabled:opacity-60">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit report
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
